import type { QuestionDetails, TagMap } from '../../../types/helios/Question';
import type { ReviewQuestionConfiguration } from '../../features/audit-whiz/logic/reviewTypes';
import { isHtmlContentEmpty } from 'src/validations/translationFormSchema';

/**
 * Pre-publish validation for a question's CONFIGURATION and AUDIT CRITERIA on
 * the AuditWhiz Review flow — the question-level counterpart of
 * {@link validateSubQuestions}. The review flow edits live (no per-field Save),
 * so these rules are enforced by the publish action: the publish gate reads
 * them, the step fields flag inline, the top banner names them, and this module
 * is the single source of truth all three read from.
 *
 * Faithful ports of the V1 manual-flow `QuestionFormValidator` rules
 * (`src/validations/QuestionFormValidator.ts`): overview/division/audit-score
 * required, at least one tag + all mandatory tag groups present, and the five
 * audit-criteria rich-text fields required. House style mirrors
 * {@link subQuestionValidation}: pure functions returning ARRAYS of typed
 * violation records, plus per-field selectors. No react, no side effects.
 */

// ─── Question configuration ──────────────────────────────────────────

/** Discriminated reasons a question's CONFIGURATION isn't ready to publish.
 *  Each is a faithful port of a V1 `QuestionFormValidator` rule. */
export const QuestionConfigViolationKind = {
  /** The Overview (question title) is blank. */
  EMPTY_OVERVIEW: 'EMPTY_OVERVIEW',
  /** No Division is selected. */
  EMPTY_DIVISION: 'EMPTY_DIVISION',
  /** No Audit Score is set (non-positive counts as unset — V1 requires a
   *  chosen score, and the widget's range starts at 1). */
  EMPTY_AUDIT_SCORE: 'EMPTY_AUDIT_SCORE',
  /** No tags are included at all (V1: "at least one tag"). */
  MISSING_TAGS: 'MISSING_TAGS',
  /** One or more tag groups flagged mandatory-for-questions is not included
   *  (V1: "all mandatory tags"). */
  MISSING_MANDATORY_TAGS: 'MISSING_MANDATORY_TAGS',
} as const;

export type QuestionConfigViolationKind =
  (typeof QuestionConfigViolationKind)[keyof typeof QuestionConfigViolationKind];

/** One configuration violation. Config is a single entity (not a list), so —
 *  unlike {@link SubQuestionViolation} — there is no index/title. */
export interface QuestionConfigViolation {
  readonly kind: QuestionConfigViolationKind;
}

/** Per-field inline-error flags for the configuration fields. Each is true when
 *  that field is blocking publish, so the field shows its native red error
 *  state. `tags` is true for EITHER tag violation (none / missing mandatory). */
export interface QuestionConfigFieldErrors {
  readonly overview: boolean;
  readonly division: boolean;
  readonly auditScore: boolean;
  readonly tags: boolean;
}

/** The ordered config rule set. Each entry pairs its `kind` with a `detect`
 *  returning true when the rule is BROKEN. `mandatoryTagGroups` carries the
 *  tag-group names flagged mandatory-for-questions (from the tag catalog), so
 *  the mandatory-tag rule stays pure (no fetch inside the validator). */
const CONFIG_RULES: readonly {
  readonly kind: QuestionConfigViolationKind;
  readonly detect: (
    config: ReviewQuestionConfiguration,
    mandatoryTagGroups: readonly string[],
  ) => boolean;
}[] = [
  {
    kind: QuestionConfigViolationKind.EMPTY_OVERVIEW,
    detect: (config) => config.overview.trim().length === 0,
  },
  {
    kind: QuestionConfigViolationKind.EMPTY_DIVISION,
    detect: (config) => config.divisionName.trim().length === 0,
  },
  {
    // V1 requires a chosen score; the widget's range is 1..max, so a
    // non-positive value means "unset".
    kind: QuestionConfigViolationKind.EMPTY_AUDIT_SCORE,
    detect: (config) => !(config.auditScore > 0),
  },
  {
    kind: QuestionConfigViolationKind.MISSING_TAGS,
    detect: (config) => includedTagGroupNames(config.tagsIncluded).size === 0,
  },
  {
    kind: QuestionConfigViolationKind.MISSING_MANDATORY_TAGS,
    detect: (config, mandatoryTagGroups) => {
      const included = includedTagGroupNames(config.tagsIncluded);
      // Only meaningful once at least one tag exists — when NO tags are
      // included the MISSING_TAGS rule already covers it.
      return (
        included.size > 0 && !mandatoryTagGroups.every((group) => included.has(group))
      );
    },
  },
];

/** The set of tag-group names that are included with at least one value.
 *  Built from `Object.entries` (not dynamic bracket indexing) so a caller's
 *  group name is never used as an object-injection sink. */
function includedTagGroupNames(tags: TagMap): ReadonlySet<string> {
  return new Set(
    Object.entries(tags)
      .filter(([, values]) => values.length > 0)
      .map(([group]) => group),
  );
}

/**
 * Every publish-blocking configuration violation, in rule order. Empty array
 * === configuration is publishable. `mandatoryTagGroups` is the set of tag
 * groups the catalog flags mandatory-for-questions (derive from the tags
 * query); pass `[]` when unknown/loading so the mandatory rule can't
 * false-positive before the catalog resolves.
 */
export function validateQuestionConfig(
  config: ReviewQuestionConfiguration,
  mandatoryTagGroups: readonly string[] = [],
): QuestionConfigViolation[] {
  const violations: QuestionConfigViolation[] = [];
  for (const rule of CONFIG_RULES) {
    if (rule.detect(config, mandatoryTagGroups)) {
      violations.push({ kind: rule.kind });
    }
  }
  return violations;
}

/**
 * Per-field error flags for the configuration fields, derived from the SAME
 * {@link validateQuestionConfig} output the gate and banner read — so an inline
 * field error, the publish gate, and the banner can never disagree. All false
 * when there are no violations; each clears the instant its field is filled
 * because the whole result is recomputed live.
 */
export function questionConfigFieldErrors(
  violations: readonly QuestionConfigViolation[],
): QuestionConfigFieldErrors {
  const has = (kind: QuestionConfigViolationKind): boolean =>
    violations.some((v) => v.kind === kind);
  return {
    overview: has(QuestionConfigViolationKind.EMPTY_OVERVIEW),
    division: has(QuestionConfigViolationKind.EMPTY_DIVISION),
    auditScore: has(QuestionConfigViolationKind.EMPTY_AUDIT_SCORE),
    tags:
      has(QuestionConfigViolationKind.MISSING_TAGS) ||
      has(QuestionConfigViolationKind.MISSING_MANDATORY_TAGS),
  };
}

// ─── Audit criteria ──────────────────────────────────────────────────

/** Discriminated reasons a question's AUDIT CRITERIA isn't ready to publish —
 *  one per required rich-text field (all five are required in V1). */
export const CriteriaViolationKind = {
  EMPTY_QUESTION_TEXT: 'EMPTY_QUESTION_TEXT',
  EMPTY_AUDIT_INSTRUCTION: 'EMPTY_AUDIT_INSTRUCTION',
  EMPTY_PASS_CRITERIA: 'EMPTY_PASS_CRITERIA',
  EMPTY_FAIL_CRITERIA: 'EMPTY_FAIL_CRITERIA',
  EMPTY_JUSTIFICATION: 'EMPTY_JUSTIFICATION',
} as const;

export type CriteriaViolationKind =
  (typeof CriteriaViolationKind)[keyof typeof CriteriaViolationKind];

/** One audit-criteria violation. */
export interface CriteriaViolation {
  readonly kind: CriteriaViolationKind;
}

/** Per-field inline-error flags for the five audit-criteria fields. */
export interface CriteriaFieldErrors {
  readonly questionText: boolean;
  readonly auditInstruction: boolean;
  readonly passCriteria: boolean;
  readonly failCriteria: boolean;
  readonly justification: boolean;
}

/** The ordered criteria rule set — one required-rule per rich-text field. Each
 *  reuses the canonical {@link isHtmlContentEmpty} (strict — no image-only) so a
 *  visually-cleared editor storing "<p></p>" is treated as blank, matching the
 *  sub-question instruction rule. */
const CRITERIA_RULES: readonly {
  readonly kind: CriteriaViolationKind;
  readonly field: keyof CriteriaFieldErrors;
}[] = [
  { kind: CriteriaViolationKind.EMPTY_QUESTION_TEXT, field: 'questionText' },
  { kind: CriteriaViolationKind.EMPTY_AUDIT_INSTRUCTION, field: 'auditInstruction' },
  { kind: CriteriaViolationKind.EMPTY_PASS_CRITERIA, field: 'passCriteria' },
  { kind: CriteriaViolationKind.EMPTY_FAIL_CRITERIA, field: 'failCriteria' },
  { kind: CriteriaViolationKind.EMPTY_JUSTIFICATION, field: 'justification' },
];

/**
 * Every publish-blocking audit-criteria violation, in field order. Empty array
 * === criteria are publishable. All five fields are rich-text, so each is
 * checked with {@link isHtmlContentEmpty} (strict: an image-only field is not
 * valid text — same as the sub-question instruction rule).
 */
export function validateCriteria(questionDetails: QuestionDetails): CriteriaViolation[] {
  return CRITERIA_RULES.filter((rule) =>
    isHtmlContentEmpty(questionDetails[rule.field]),
  ).map((rule) => ({ kind: rule.kind }));
}

/**
 * Per-field error flags for the five criteria fields, derived from the SAME
 * {@link validateCriteria} output the gate and banner read.
 */
export function criteriaFieldErrors(
  violations: readonly CriteriaViolation[],
): CriteriaFieldErrors {
  const has = (kind: CriteriaViolationKind): boolean =>
    violations.some((v) => v.kind === kind);
  return {
    questionText: has(CriteriaViolationKind.EMPTY_QUESTION_TEXT),
    auditInstruction: has(CriteriaViolationKind.EMPTY_AUDIT_INSTRUCTION),
    passCriteria: has(CriteriaViolationKind.EMPTY_PASS_CRITERIA),
    failCriteria: has(CriteriaViolationKind.EMPTY_FAIL_CRITERIA),
    justification: has(CriteriaViolationKind.EMPTY_JUSTIFICATION),
  };
}
